/**
 * HTTP layer for customer records. Customer detail and types, scope-search
 * suggestions, grid stats, the deactivation and reactivation flows, and
 * third-party integration listings.
 *
 * Contains:
 * - fetchCustomer / fetchCustomerTypes: customer reads
 * - fetchCustomerScopeSuggestions: scope-search typeahead
 * - fetchCustomersStats: customer grid stats
 * - fetchCustomerDeactivatePreflight / deactivateCustomer: deactivation flow
 * - reactivateCustomer: clears the deactivated flag
 * - fetchThirdParties / fetchAvailableIntegrations / fetchIntegrations: integration reads
 */
import apiClient from '@sophocles/http-common';
import { generateQueryParams } from '@sophocles/utils';

const CUSTOMER_ENDPOINT = '/customers';
const CUSTOMER_TYPE_ENDPOINT = '/customer-types';

// Customer Type API

export const fetchCustomerTypes = queryParams =>
  apiClient.get(`${CUSTOMER_TYPE_ENDPOINT}/`, { params: queryParams }).then(res => res.data);

// Customer API

export const fetchCustomer = customerId => apiClient.get(`${CUSTOMER_ENDPOINT}/${customerId}`).then(res => res.data);

// Scope-search autocomplete: server-side text_search + pagination.
export const fetchCustomerScopeSuggestions = params =>
  apiClient.get(`${CUSTOMER_ENDPOINT}/minimal/`, { params }).then(res => res.data);

// odl-app summary stats for the customers grid — distinct base path from the
// legacy CUSTOMER_ENDPOINT above.
export const fetchCustomersStats = params =>
  apiClient.get('odl-app/customers/stats/', { params }).then(res => res.data);

// Customer deactivation — the odl-app RPC action trio. Preflight is a
// synchronous dry-run summary, including the pickable radio_destinations
// (the operator's relocate target); deactivate enqueues a background cascade
// and returns 202 immediately, so the caller polls the customer's
// `deactivated` flag (via fetchCustomer) for completion. Both are bare
// (non-enveloped) RPC responses — only the axios `res.data` layer to unwrap.

export const fetchCustomerDeactivatePreflight = customerId =>
  apiClient.get(`odl-app/customers/${customerId}/deactivate-preflight/`).then(res => res.data);

// A "inventory" relocate targets EITHER another active customer OR a global
// inventory location, mutually exclusive; destination_customer_id wins when
// both are present, matching the BE precedence. "purge" carries neither.
function deactivateDestination({ inventoryLocationId, destinationCustomerId }) {
  if (destinationCustomerId != null) {
    return { destination_customer_id: destinationCustomerId };
  }
  if (inventoryLocationId != null) {
    return { inventory_location_id: inventoryLocationId };
  }
  return {};
}

export const deactivateCustomer = (customerId, radioAction, destination = {}) =>
  apiClient
    .post(`odl-app/customers/${customerId}/deactivate/`, {
      radio_action: radioAction,
      ...deactivateDestination(destination),
    })
    .then(res => res.data);

// Reactivation clears the customer's own flag and nothing else, so unlike
// deactivate it is synchronous and there is no cascade to poll for. The
// customer's groups, sites and radios stay as the deactivation left them and
// come back through their own bulk-activate endpoints.
export const reactivateCustomer = customerId =>
  apiClient.post(`odl-app/customers/${customerId}/reactivate/`).then(res => res.data);

// Third Party Management

export const fetchThirdParties = (customerId, queryParams = {}) =>
  apiClient
    .get(`${CUSTOMER_ENDPOINT}/${customerId}/thirdparties/`, { params: generateQueryParams({ ...queryParams }) })
    .then(res => res.data);

// Integrations

export const fetchAvailableIntegrations = (customerId, queryParams = {}) =>
  apiClient
    .get(`${CUSTOMER_ENDPOINT}/${customerId}/availableintegrations/`, { params: generateQueryParams(queryParams) })
    .then(res => res.data);

export const fetchIntegrations = (customerId, queryParams = {}) =>
  apiClient
    .get(`${CUSTOMER_ENDPOINT}/${customerId}/integrations/`, { params: generateQueryParams(queryParams) })
    .then(res => res.data);
